// AI Testing Service - Database interactions for test cases and sessions
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  TestCase,
  TestSession,
  ModelResponse,
  AgentType,
  ModelType
} from '@/types/ai-admin';

const log = createComponentLogger('AITestingService', 'ai-testing');

// ============================================================================
// Test Cases
// ============================================================================

/**
 * Get all test cases for the current organization
 */
export async function getTestCases(): Promise<TestCase[]> {
  try {
    log.info('Loading test cases');

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Load test cases
    const { data: testCases, error } = await supabase
      .from('ai_test_cases')
      .select('*')
      .eq('organization_id', userData.organization_id)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}

    // Map to frontend format
    const mapped = (testCases || []).map(tc => ({
      id: tc.id,
      name: tc.name,
      description: tc.description || '',
      query: tc.query,
      expectedKeywords: tc.expected_keywords || [],
      agentType: tc.agent_type as AgentType,
      tags: tc.tags || [],
      createdAt: tc.created_at,
      lastUsed: tc.last_used_at,
      runCount: tc.run_count,
      avgScore: tc.avg_score
    }));

    log.info('Test cases loaded', { count: mapped.length });
    return mapped;
  } catch (error) {
    log.error('Failed to load test cases', {}, error as Error);
    throw error;
  }
}

/**
 * Create a new test case
 */
export async function createTestCase(testCase: Partial<TestCase>): Promise<TestCase> {
  try {
    log.info('Creating test case', { name: testCase.name });

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Insert test case
    const { data: newTestCase, error } = await supabase
      .from('ai_test_cases')
      .insert({
        organization_id: userData.organization_id,
        name: testCase.name,
        description: testCase.description || null,
        query: testCase.query,
        expected_keywords: testCase.expectedKeywords || [],
        agent_type: testCase.agentType,
        tags: testCase.tags || [],
        created_by: user.id,
        run_count: 0,
        avg_score: null
      })
      .select()
      .single();

    if (error) {
throw error;
}

    log.info('Test case created', { id: newTestCase.id });

    return {
      id: newTestCase.id,
      name: newTestCase.name,
      description: newTestCase.description || '',
      query: newTestCase.query,
      expectedKeywords: newTestCase.expected_keywords || [],
      agentType: newTestCase.agent_type as AgentType,
      tags: newTestCase.tags || [],
      createdAt: newTestCase.created_at,
      lastUsed: null,
      runCount: 0,
      avgScore: null
    };
  } catch (error) {
    log.error('Failed to create test case', {}, error as Error);
    throw error;
  }
}

/**
 * Update an existing test case
 */
export async function updateTestCase(
  id: string,
  updates: Partial<TestCase>
): Promise<TestCase> {
  try {
    log.info('Updating test case', { id });

    const dbUpdates: any = {};
    if (updates.name) {
dbUpdates.name = updates.name;
}
    if (updates.description !== undefined) {
dbUpdates.description = updates.description;
}
    if (updates.query) {
dbUpdates.query = updates.query;
}
    if (updates.expectedKeywords) {
dbUpdates.expected_keywords = updates.expectedKeywords;
}
    if (updates.agentType) {
dbUpdates.agent_type = updates.agentType;
}
    if (updates.tags) {
dbUpdates.tags = updates.tags;
}

    const { data: updated, error } = await supabase
      .from('ai_test_cases')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
throw error;
}

    log.info('Test case updated', { id });

    return {
      id: updated.id,
      name: updated.name,
      description: updated.description || '',
      query: updated.query,
      expectedKeywords: updated.expected_keywords || [],
      agentType: updated.agent_type as AgentType,
      tags: updated.tags || [],
      createdAt: updated.created_at,
      lastUsed: updated.last_used_at,
      runCount: updated.run_count,
      avgScore: updated.avg_score
    };
  } catch (error) {
    log.error('Failed to update test case', { id }, error as Error);
    throw error;
  }
}

/**
 * Delete a test case (soft delete by marking inactive)
 */
export async function deleteTestCase(id: string): Promise<void> {
  try {
    log.info('Deleting test case', { id });

    const { error } = await supabase
      .from('ai_test_cases')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
throw error;
}

    log.info('Test case deleted', { id });
  } catch (error) {
    log.error('Failed to delete test case', { id }, error as Error);
    throw error;
  }
}

// ============================================================================
// Test Sessions
// ============================================================================

/**
 * Get test session history
 */
export async function getTestSessions(limit: number = 50): Promise<TestSession[]> {
  try {
    log.info('Loading test sessions', { limit });

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Load sessions
    const { data: sessions, error } = await supabase
      .from('ai_test_sessions')
      .select('*')
      .eq('organization_id', userData.organization_id)
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) {
throw error;
}

    // Load responses for each session
    const sessionsWithResponses = await Promise.all(
      (sessions || []).map(async (session) => {
        const { data: responses } = await supabase
          .from('ai_test_responses')
          .select('*')
          .eq('session_id', session.id)
          .order('created_at', { ascending: true });

        return {
          id: session.id,
          timestamp: session.started_at,
          testCases: session.test_case_ids,
          responses: (responses || []).map(r => ({
            id: r.id,
            testCaseId: r.test_case_id,
            query: r.query,
            model: r.model,
            response: r.response_text,
            responseTime: r.response_time_ms,
            cost: r.cost_usd,
            tokensInput: r.tokens_input,
            tokensOutput: r.tokens_output,
            success: r.success,
            score: r.score,
            documentsUsed: r.documents_used || [],
            timestamp: r.created_at,
            flagged: r.flagged,
            flagReason: r.flag_reason
          })),
          totalDuration: session.total_duration_ms || 0,
          totalCost: Number(session.total_cost_usd),
          successRate: Number(session.success_rate || 0)
        };
      })
    );

    log.info('Test sessions loaded', { count: sessionsWithResponses.length });
    return sessionsWithResponses;
  } catch (error) {
    log.error('Failed to load test sessions', {}, error as Error);
    throw error;
  }
}

/**
 * Create a new test session
 */
export async function createTestSession(
  testCaseIds: string[],
  sessionType: 'manual' | 'automated' | 'comparison' = 'manual'
): Promise<string> {
  try {
    log.info('Creating test session', { testCaseIds, sessionType });

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Create session
    const { data: session, error } = await supabase
      .from('ai_test_sessions')
      .insert({
        organization_id: userData.organization_id,
        test_case_ids: testCaseIds,
        session_type: sessionType,
        created_by: user.id
      })
      .select()
      .single();

    if (error) {
throw error;
}

    log.info('Test session created', { sessionId: session.id });
    return session.id;
  } catch (error) {
    log.error('Failed to create test session', {}, error as Error);
    throw error;
  }
}

/**
 * Update test session results
 */
export async function updateTestSession(
  sessionId: string,
  updates: {
    totalDuration?: number;
    totalCost?: number;
    successRate?: number;
  }
): Promise<void> {
  try {
    log.info('Updating test session', { sessionId });

    const dbUpdates: any = {
      completed_at: new Date().toISOString()
    };

    if (updates.totalDuration !== undefined) {
      dbUpdates.total_duration_ms = updates.totalDuration;
    }
    if (updates.totalCost !== undefined) {
      dbUpdates.total_cost_usd = updates.totalCost;
    }
    if (updates.successRate !== undefined) {
      dbUpdates.success_rate = updates.successRate;
    }

    const { error } = await supabase
      .from('ai_test_sessions')
      .update(dbUpdates)
      .eq('id', sessionId);

    if (error) {
throw error;
}

    log.info('Test session updated', { sessionId });
  } catch (error) {
    log.error('Failed to update test session', { sessionId }, error as Error);
    throw error;
  }
}

/**
 * Add test response to session
 */
export async function addTestResponse(
  sessionId: string,
  response: ModelResponse
): Promise<void> {
  try {
    log.info('Adding test response', { sessionId, testCaseId: response.testCaseId });

    const { error } = await supabase
      .from('ai_test_responses')
      .insert({
        session_id: sessionId,
        test_case_id: response.testCaseId || null,
        query: response.query,
        model: response.model,
        agent_type: 'general', // Will be passed in the response object in real implementation
        response_text: response.response,
        response_time_ms: response.responseTime,
        cost_usd: response.cost,
        tokens_input: response.tokensInput,
        tokens_output: response.tokensOutput,
        success: response.success,
        score: response.score,
        documents_used: response.documentsUsed || [],
        flagged: response.flagged || false,
        flag_reason: response.flagReason
      });

    if (error) {
throw error;
}

    // Update test case statistics
    if (response.testCaseId && response.score !== undefined) {
      await updateTestCaseStats(response.testCaseId, response.score);
    }

    log.info('Test response added', { sessionId });
  } catch (error) {
    log.error('Failed to add test response', { sessionId }, error as Error);
    throw error;
  }
}

/**
 * Update test case statistics after a run
 */
async function updateTestCaseStats(testCaseId: string, score: number): Promise<void> {
  try {
    // Get current stats
    const { data: testCase } = await supabase
      .from('ai_test_cases')
      .select('run_count, avg_score')
      .eq('id', testCaseId)
      .single();

    if (!testCase) {
return;
}

    const newRunCount = testCase.run_count + 1;
    const currentAvg = testCase.avg_score || 0;
    const newAvg = (currentAvg * testCase.run_count + score) / newRunCount;

    // Update stats
    await supabase
      .from('ai_test_cases')
      .update({
        run_count: newRunCount,
        avg_score: newAvg,
        last_used_at: new Date().toISOString()
      })
      .eq('id', testCaseId);

    log.info('Test case stats updated', { testCaseId, newAvg, newRunCount });
  } catch (error) {
    log.error('Failed to update test case stats', { testCaseId }, error as Error);
    // Don't throw - this is a best-effort update
  }
}

/**
 * Call the ai-test-query edge function
 */
export async function callTestQueryEdgeFunction(
  query: string,
  model: ModelType,
  agentType: AgentType,
  sessionId: string,
  testCaseId?: string
): Promise<ModelResponse> {
  try {
    log.info('Calling test query edge function', { model, agentType });

    const { data, error } = await supabase.functions.invoke('ai-test-query', {
      body: {
        query,
        model,
        agent_type: agentType,
        session_id: sessionId,
        test_case_id: testCaseId || null
      }
    });

    if (error) {
throw error;
}

    if (!data || !data.success) {
      throw new Error(data?.error || 'Edge function call failed');
    }

    // Map edge function response to ModelResponse format
    const response: ModelResponse = {
      id: `resp_${Date.now()}`,
      testCaseId,
      query,
      model,
      response: data.response,
      responseTime: data.responseTime,
      cost: data.cost,
      tokensInput: data.tokensInput,
      tokensOutput: data.tokensOutput,
      success: true,
      documentsUsed: data.documentsUsed || [],
      timestamp: new Date().toISOString()
    };

    log.info('Test query completed', {
      responseTime: data.responseTime,
      cost: data.cost
    });

    return response;
  } catch (error) {
    log.error('Edge function call failed', {}, error as Error);
    throw error;
  }
}

/**
 * Run a test query for a specific test case
 */
export async function runTestQuery(
  testCaseId: string,
  model: ModelType,
  agentType: AgentType,
  sessionId: string
): Promise<ModelResponse> {
  try {
    log.info('Running test query', { testCaseId, model });

    // Get the test case
    const { data: testCase } = await supabase
      .from('ai_test_cases')
      .select('query')
      .eq('id', testCaseId)
      .single();

    if (!testCase) {
      throw new Error('Test case not found');
    }

    // Call edge function
    const response = await callTestQueryEdgeFunction(
      testCase.query,
      model,
      agentType,
      sessionId,
      testCaseId
    );

    // Store response in database
    await addTestResponse(sessionId, response);

    return response;
  } catch (error) {
    log.error('Failed to run test query', { testCaseId }, error as Error);
    throw error;
  }
}
